import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  ChangeDetectorRef,
  OnChanges,
  EventEmitter,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { navItems } from './sidebar-data';
import { Router } from '@angular/router';
import { NavService } from '../../../../shared/services/nav.service';
import { MediaMatcher } from '@angular/cdk/layout';
import { AppHorizontalNavItemComponent } from './nav-item/nav-item.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { HasPermissionDirective } from 'src/app/directives/has-permission.directive';
import { MaterialModule } from 'src/app/material.module';
import { BrandingComponent } from '../../vertical/sidebar/branding.component';
import { AppNavItemComponent } from '../../vertical/sidebar/nav-item/nav-item.component';

@Component({
  selector: 'app-horizontal-sidebar',
  imports: [AppHorizontalNavItemComponent, CommonModule, BrandingComponent, TablerIconsModule, MaterialModule,
    FormsModule,
    AppNavItemComponent,
    NgScrollbarModule,
    HasPermissionDirective,
    TranslateModule],
  templateUrl: './sidebar.component.html',
})
export class AppHorizontalSidebarComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() showToggle = true;
  @Input() items: any[] = [];
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  @ViewChild('navScroll') navScroll!: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  canScrollLeft  = false;
  canScrollRight = false;

  navItems = navItems;
  parentActive = '';

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  searchText: string = '';
  searchOpen = false;
  filteredItems: any[] = [];
  private langCode: string = 'ar';
  userId: number;

  constructor(
    public navService: NavService,
    public router: Router,
    media: MediaMatcher,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    // Check initial scroll state after first render
    setTimeout(() => this.updateScrollState());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateScrollState();
  }

  onNavScroll(): void {
    this.updateScrollState();
  }

  get isRTL(): boolean {
    return document.documentElement.dir === 'rtl' || document.body.classList.contains('rtl');
  }

  scrollNav(delta: number): void {
    const el = this.navScroll?.nativeElement;
    if (!el) return;
    // In RTL, Chrome stores scrollLeft as 0 → negative, so flip the direction
    const isRTL = document.documentElement.dir === 'rtl' || document.body.classList.contains('rtl');
    el.scrollBy({ left: isRTL ? -delta : delta, behavior: 'smooth' });
    setTimeout(() => this.updateScrollState(), 350);
  }

  private updateScrollState(): void {
    const el = this.navScroll?.nativeElement;
    if (!el) return;
    const prev = { l: this.canScrollLeft, r: this.canScrollRight };
    // Chrome RTL: scrollLeft is 0 at natural start, goes negative when scrolled left.
    // Normalize to a non-negative position so the same comparisons work for both directions.
    const scrollPos = Math.abs(el.scrollLeft);
    this.canScrollLeft  = scrollPos > 2;
    this.canScrollRight = scrollPos + el.clientWidth < el.scrollWidth - 2;
    if (prev.l !== this.canScrollLeft || prev.r !== this.canScrollRight) {
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    const lang = localStorage.getItem('lang');
    if (lang) {
      try {
        const langObj = JSON.parse(lang);
        this.langCode = langObj.code || 'ar';
      } catch {
        this.langCode = 'ar';
      }
    }

    this.initializeMenu();
  }

  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && changes['items'].currentValue) {
      this.initializeMenu();
      setTimeout(() => this.updateScrollState(), 120);
    }
  }
  
  private initializeMenu(): void {
    if (this.items && this.items.length > 0) {
      this.patchNames(this.items);

      // If search text exists, apply filtering immediately
      if (this.searchText.trim()) {
        this.onSearchChange(this.searchText);
      } else {
        this.filteredItems = [...this.items];
      }
    }
  }

  patchNames(items: any[]) {
    items.forEach(item => {
      if (!item.nameEn) item.nameEn = item.displayName;
      if (!item.nameAr) item.nameAr = item.displayName;

      if (item.children) this.patchNames(item.children);
    });
  }

  toggleSearch(): void {
    if (this.searchOpen) {
      this.closeSearch();
      return;
    }
    this.searchOpen = true;
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  closeSearch(): void {
    this.searchOpen = false;
    if (this.searchText) {
      this.searchText = '';
      this.onSearchChange('');
    }
  }

  onSearchChange(text: string) {
    const lower = text.toLowerCase().trim();

    if (!lower) {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.filterMenu(this.items, lower);
    }

    // Item count changed → recompute the scroll arrows
    setTimeout(() => this.updateScrollState(), 60);
  }

  filterMenu(items: any[], search: string): any[] {
    return items
      .map(item => {
        const nameEn = item.nameEn?.toLowerCase() ?? '';
        const nameAr = item.nameAr?.toLowerCase() ?? '';
        const displayName = item.displayName?.toLowerCase() ?? '';

        // 🔍 Search in all name fields
        const matches =
          nameEn.includes(search) ||
          nameAr.includes(search) ||
          displayName.includes(search);

        if (matches) {
          return { ...item, expanded: true };
        }

        if (item.children && item.children.length > 0) {
          const kids = this.filterMenu(item.children, search);

          // If children contain matches, keep them
          if (kids.length > 0) {
            return {
              ...item,
              children: kids,
              expanded: true
            };
          }
        }

        return null;
      })
      .filter(x => x !== null) as any[];
  }
}
